import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useGetPulsesQuery } from './pulse.endpoints';
import {
  cadenceLabel,
  confidenceBadgeClass,
  formatConfidence,
  formatDirectionDelta,
  formatGeography,
  formatPositionValue,
  formatRelativeTime,
  formatVertical,
  pulseSurveyPath,
  statusBadgeClass,
} from './pulse.helpers';
import { PulseListItem } from './pulse.types';

function MetricCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
    </div>
  );
}

function PositionCell({ pulse }: { pulse: PulseListItem }) {
  const position = pulse.position;
  if (!position) {
    return <span className="text-gray-400">—</span>;
  }
  const delta = formatDirectionDelta(position.direction, position);
  const toneClass =
    delta.tone === 'up'
      ? 'text-emerald-600'
      : delta.tone === 'down'
        ? 'text-red-600'
        : 'text-gray-500';
  return (
    <span className="font-medium text-gray-900">
      {formatPositionValue(position)}{' '}
      {delta.label ? <span className={toneClass}>{delta.label}</span> : null}
    </span>
  );
}

const PulseTab = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const { data, isLoading, isError } = useGetPulsesQuery();
  const [filter, setFilter] = useState('');

  const pulses = data?.pulses ?? [];
  const aggregates = data?.aggregates;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return pulses;
    return pulses.filter(
      (pulse) =>
        pulse.name.toLowerCase().includes(q) ||
        pulse.vertical.toLowerCase().includes(q) ||
        pulse.slug.toLowerCase().includes(q),
    );
  }, [filter, pulses]);

  const lastTick = formatRelativeTime(aggregates?.last_tick);
  const backfillHint =
    aggregates?.backfill_queue?.windows && aggregates.backfill_queue.windows > 0
      ? `${aggregates.backfill_queue.windows} windows${
          aggregates.backfill_queue.issue_name
            ? ` · ${aggregates.backfill_queue.issue_name}`
            : ''
        }`
      : 'Empty';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            National &amp; regional
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Pulses</h1>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Find</span>
            <Input
              className="w-56"
              placeholder="Filter pulses"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <Button
            variant="black"
            size="med"
            onClick={() => navigate(pulseSurveyPath('pulse-create', clientId))}
          >
            New pulse
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard label="Active" value={String(aggregates?.active ?? 0)} />
        <MetricCard label="Paused" value={String(aggregates?.paused ?? 0)} />
        <MetricCard label="Last tick" value={lastTick} />
        <MetricCard label="Backfill queue" value={backfillHint} />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead>Pulse</TableHead>
              <TableHead>Vertical</TableHead>
              <TableHead>Geography</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-gray-500">
                  Loading pulses…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={7} className="text-red-600">
                  Unable to load pulses.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-gray-500">
                  No pulses yet. Create one to start measuring.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((pulse) => (
              <TableRow
                key={pulse.id}
                className="cursor-pointer"
                onClick={() =>
                  navigate(pulseSurveyPath('pulse-detail', clientId, `id=${pulse.id}`))
                }
              >
                <TableCell>
                  <div className="font-medium text-blue-600">{pulse.name}</div>
                  <div className="text-xs text-gray-500">{cadenceLabel(pulse)}</div>
                </TableCell>
                <TableCell>{formatVertical(pulse.vertical)}</TableCell>
                <TableCell>{formatGeography(pulse.geography_scope)}</TableCell>
                <TableCell>
                  <PositionCell pulse={pulse} />
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs ${confidenceBadgeClass(
                      pulse.position?.confidence_tier,
                    )}`}
                  >
                    {formatConfidence(pulse.position?.confidence_tier)}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {formatRelativeTime(pulse.last_run?.finished_at ?? pulse.last_run?.window_end)}
                  {pulse.last_run?.status === 'partial' ? ' · partial' : ''}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium uppercase ${statusBadgeClass(
                      pulse.status,
                    )}`}
                  >
                    {pulse.status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-gray-500">
        A paused pulse leaves a real gap in its series. Resuming does not backfill it — trigger a
        backfill explicitly if the gap matters.
      </p>
    </div>
  );
};

export default PulseTab;
